import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { List, ListHeading, ListSubheading, Spinner } from '@oliasoft-open-source/react-ui-library';
import translations from 'src/internationalisation/translation-map.json';
import { operationSelected, reorderOperations } from 'store/entities/activity-model/activity-model';
import { selectSectionsDataStructure } from 'src/store/entities/sections-data-structure/selector';
import { selectOperationListByActiveSection } from 'src/store/entities/activity-model/selectors';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { convertToKebabCase } from 'client/common/utils/string-utils.ts';
import { Badge } from '@oliasoft-open-source/react-ui-library';
import { round, unum } from '@oliasoft-open-source/units';
import { meanOperation } from 'src/views/cost-setup/helpers/precalculate-time';

const OperationsList = ({
  operations,
  activeSection,
  operationSelected,
  reorderOperations,
  setActiveForm,
  setOperationModalVisible,
  onClickAddOperation,
  setSelectedId,
  isAdding,
  expandAllItems,
  isPageDisabled,
  activeOperations,
  sectionItems,
  operationsByActiveSession,
  handleOperationsImportModal,
  setIsImportFromSection,
  showMetrics,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (Array.isArray(operations) && operations.length > 1) {
      reorderOperations(null, operations, sectionItems);
    }
  }, [sectionItems]);

  const onClickDelete = (e, id) => {
    setSelectedId(e.shiftKey ? null : id);
    setOperationModalVisible(true);
  };

  const onClickItem = async (id) => {
    await operationSelected(id);
    await setActiveForm('operation');
  };
  const operationName = activeOperations.filter((op) => op.projectSectionId === activeSection.projectSectionId)[0]
    ?.name;

  const disabled = isAdding || isPageDisabled;

  const items = operations
    .filter((operation) => operation.projectSectionId === activeSection.projectSectionId)
    .map((operation) => {
      const activityCount = (operation.tasks ?? []).length;
      const meanDays = round(unum(meanOperation(operation), 'd', 'h'), 2);
      return {
        testId: `operations-list-${convertToKebabCase(operation.name)}-item`,
        id: operation.operationId,
        name: operation.name,
        draggable: true,
        active: operation.active,
        onClick: () => onClickItem(operation.operationId),
        actions: [
          ...(showMetrics
            ? [
                {
                  childComponent: (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--padding-sm)',
                      }}
                    >
                      <span
                        style={{
                          whiteSpace: 'nowrap',
                          color: 'var(--color-text-success)',
                        }}
                      >
                        {`${new Intl.NumberFormat('en-US').format(meanDays)} ${t(translations.days)}`}
                      </span>
                      <Badge
                        title={String(activityCount)}
                        color={activityCount ? '#cccccc' : ''}
                        textColor={'#ffffff'}
                      />
                    </div>
                  ),
                },
              ]
            : []),
          {
            testId: `operations-list-${convertToKebabCase(operation.name)}-delete-button`,
            label: 'Delete',
            icon: 'delete',
            onClick: (e) => onClickDelete(e, operation.operationId),
            disabled,
          },
        ],
      };
    });

  return (
    <>
      <ListHeading name={activeSection.name} />
      <ListSubheading
        item={{
          name: 'Operations',
          actions: [
            {
              label: 'Manage',
              icon: isAdding ? <Spinner tiny dark /> : 'add',
              testId: 'operations-list-subheading-more-button',
              subActions: [
                {
                  label: t(translations.add),
                  testId: 'operations-list-subheading-add-button',
                  onClick: () => onClickAddOperation(activeSection.projectSectionId, null, operationName),
                  disabled,
                },
                {
                  label: t(translations.activityModel_importFromSection),
                  onClick: () => {
                    handleOperationsImportModal(true);
                    setIsImportFromSection(true);
                  },
                  disabled,
                },
              ],
            },
          ],
        }}
      />
      <List
        list={{
          items,
        }}
        draggable={!isPageDisabled}
        noHeader
        onListReorder={({ from, to }) => {
          if (from === undefined || to === undefined) {
            return;
          }
          reorderOperations(
            { from, to },
            operations,
            sectionItems,
            operationsByActiveSession,
            activeSection.projectSectionId,
          );
        }}
      />
    </>
  );
};

const mapStateToProps = ({ entities }) => {
  const { operations: activeOperations } = selectSectionsDataStructure(entities);

  return {
    operations: entities.activityModel.operations,
    operationsByActiveSession: selectOperationListByActiveSection({ entities }),
    isFetching: entities.wellDetails.isFetching,
    activeOperations,
    sectionItems: entities.activityModel.sectionItems,
  };
};

const mapDispatchToProps = {
  operationSelected,
  reorderOperations,
};

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(OperationsList));

export { Container as OperationsList };
